"use client";

import type { ReactNode } from "react";
import { TrackedAction } from "@/components/analytics/TrackedAction";
import type { PhysicianTier } from "@/lib/data/physician-tier";
import { track } from "@/lib/observability/analytics";

type Props = {
  channel: "phone" | "whatsapp";
  href: string;
  physicianSlug: string;
  source: "rdv-unavailable" | "rdv-similar";
  tier: PhysicianTier;
  city: string;
  /** Fiche /rdv depuis laquelle ce praticien similaire est proposé. */
  fromSlug?: string;
  className?: string;
  children: ReactNode;
};

type RdvContactTarget = Pick<Props, "physicianSlug" | "source" | "tier" | "city" | "fromSlug">;

type RdvCallTrackEvent = {
  type: "PHONE_CLICK";
  entityType: "Physician";
  entitySlug: string;
  source: string;
  metadata: Record<string, string>;
};

function contactMetadata({ tier, city, fromSlug }: RdvContactTarget): Record<string, string> {
  return fromSlug ? { tier, city, from: fromSlug } : { tier, city };
}

/**
 * Les deux PHONE_CLICK d'un appel passé depuis /rdv, dans l'ordre d'envoi :
 * l'intention (call-cabinet:…), que comptent les Stats Pro, puis le clic sur
 * le numéro (call-completed:…), qui confirme l'intention sans la recompter
 * (lib/analytics/human-clicks.ts, isCallIntent). Même paire et même entitySlug
 * (Physician.slug, interrogé par /espace-pro/stats) qu'UnclaimedCallButton en
 * mode direct.
 *
 * Bug 2026-10-06 : seule la confirmation partait, ces appels n'apparaissaient
 * jamais dans les Stats Pro du médecin appelé.
 */
export function rdvCallTrackEvents(target: RdvContactTarget): {
  intent: RdvCallTrackEvent;
  completed: RdvCallTrackEvent;
} {
  const base: Omit<RdvCallTrackEvent, "source"> = {
    type: "PHONE_CLICK",
    entityType: "Physician",
    entitySlug: target.physicianSlug,
    metadata: contactMetadata(target),
  };
  return {
    intent: { ...base, source: `call-cabinet:${target.source}` },
    completed: { ...base, source: `call-completed:${target.source}` },
  };
}

/** Même envoi que TrackedAction : sendBeacon survit au départ vers le composeur. */
function sendTrackEvent(event: RdvCallTrackEvent): void {
  const body = JSON.stringify(event);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon("/api/track/event", new Blob([body], { type: "application/json" }))) return;
    }
    fetch("/api/track/event", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  } catch {
    // le suivi ne doit jamais bloquer l'appel
  }
}

/**
 * Lien d'appel ou WhatsApp de la page /rdv, suivi en base et dans
 * Plausible/GA4 (2026-10-05) : sans track(), ces contacts manquaient dans
 * GA4, où se mesure la levée de l'impasse « Demander un RDV ».
 */
export function RdvContactLink({ channel, href, physicianSlug, source, tier, city, fromSlug, className, children }: Props) {
  const target = { physicianSlug, source, tier, city, fromSlug };

  if (channel === "whatsapp") {
    return (
      <TrackedAction
        event="WHATSAPP_CLICK"
        entityType="Physician"
        entitySlug={physicianSlug}
        source={source}
        metadata={contactMetadata(target)}
      >
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={() =>
            track("physician:contact_whatsapp", { source, physician_slug: physicianSlug, tier, city })
          }
        >
          {children}
        </a>
      </TrackedAction>
    );
  }

  // Lien tel: direct : un clic vaut intention puis appel, comme le mode direct
  // d'UnclaimedCallButton (base et Plausible/GA4).
  const handleCall = () => {
    const { intent, completed } = rdvCallTrackEvents(target);
    sendTrackEvent(intent);
    track("physician:call_intent", { source, physician_slug: physicianSlug });
    track("physician:contact_phone", { source, physician_slug: physicianSlug, tier, city });
    sendTrackEvent(completed);
  };

  return (
    <a href={href} className={className} onClick={handleCall}>
      {children}
    </a>
  );
}
